<template>
  <div class="mx-auto flex w-full max-w-3xl flex-col gap-4 py-5">
    <div role="table" :aria-label="tr('Payment history')" class="flex flex-col gap-2">
      <div role="row" class="grid items-center gap-x-4 max-md:grid-cols-[minmax(0,1fr)_auto] max-md:gap-y-1.5 md:grid-cols-[7rem_6rem_6rem_minmax(0,1fr)_minmax(0,1fr)_2rem] px-4 text-xs font-semibold text-muted-foreground max-md:hidden">
        <span role="columnheader">{{ tr('Date') }}</span>
        <span role="columnheader" class="text-right">{{ tr('Net') }}</span>
        <span role="columnheader" class="text-right">{{ tr('Gross') }}</span>
        <span role="columnheader">{{ tr('Status') }}</span>
        <span role="columnheader">{{ tr('Invoice number') }}</span>
        <span role="columnheader" />
      </div>

      <div
        v-for="paymentHistory in list"
        :key="paymentHistory.id"
        role="row"
        class="grid items-center gap-x-4 max-md:grid-cols-[minmax(0,1fr)_auto] max-md:gap-y-1.5 md:grid-cols-[7rem_6rem_6rem_minmax(0,1fr)_minmax(0,1fr)_2rem] glass-tile cursor-pointer rounded-xl p-4 text-sm transition-shadow duration-300 ease-in-out hover:[--tile-glow:color-mix(in_oklab,var(--primary)_40%,transparent)] md:py-2.5"
        @click="() => openDetails(paymentHistory)"
      >
        <div role="cell" class="font-medium text-foreground tabular-nums">{{ formatToYMD(paymentHistory.created_at) }}</div>
        <div role="cell" class="text-right text-muted-foreground tabular-nums max-md:hidden">{{ formatPrice(paymentHistory.net, '€') }}</div>
        <div role="cell" class="font-semibold text-foreground tabular-nums max-md:order-3 md:text-right">{{ formatPrice(paymentHistory.gross, '€') }}</div>
        <div role="cell" class="max-md:order-2 max-md:justify-self-end">
          <ui-badge :variant="statusVariant(paymentHistory.status)">
            {{ tr(paymentHistory.status) }}
          </ui-badge>
        </div>
        <div role="cell" class="truncate text-muted-foreground tabular-nums max-md:order-4 max-md:text-right">{{ paymentHistory.invoice_number || '–' }}</div>
        <div role="cell" class="max-md:hidden">
          <arrow-link :label="tr('Details')" @click.stop="() => openDetails(paymentHistory)" />
        </div>
      </div>

      <template v-if="isLoading || hasMore">
        <div
          v-for="i in (list.length ? 1 : 3)"
          :key="'payment-history-skeleton-' + i"
          ref="scrollPagerTrigger"
          role="row"
          class="grid items-center gap-x-4 max-md:grid-cols-[minmax(0,1fr)_auto] max-md:gap-y-1.5 md:grid-cols-[7rem_6rem_6rem_minmax(0,1fr)_minmax(0,1fr)_2rem] glass-tile rounded-xl p-4 md:py-2.5"
        >
          <!-- The cells of a loaded row, with placeholders of the same size. -->
          <div role="cell" class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-20" /></div>
          <div role="cell" class="flex h-5 items-center justify-end max-md:hidden"><ui-skeleton class="h-3.5 w-12" /></div>
          <div role="cell" class="flex h-5 items-center max-md:order-3 md:justify-end"><ui-skeleton class="h-3.5 w-16" /></div>
          <div role="cell" class="max-md:order-2 max-md:justify-self-end"><ui-skeleton class="h-5.5 w-16 rounded-md" /></div>
          <div role="cell" class="flex h-5 items-center max-md:order-4 max-md:justify-end"><ui-skeleton class="h-3.5 w-24" /></div>
          <div role="cell" class="max-md:hidden"><ui-skeleton class="size-8 rounded-full" /></div>
        </div>
      </template>

      <p v-if="list.length === 0 && !isLoading && !hasMore" class="m-0 px-2 py-6 text-center text-sm text-muted-foreground">
        {{ tr('There are no items to display.') }}
      </p>
    </div>
  </div>
</template>

<script>
import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import PaymentHistoryService from '@/services/PaymentHistory';
import ArrowLink from '@/ui/components/ArrowLink.vue';
import PaymentHistoryDetailsModal from '@/ui/components/PaymentHistoryDetailsModal.vue';
import { formatToYMD } from '@/utils/date';
import { formatPrice } from '@/utils/number';

const LIMIT_PER_PAGE = 20;

export default {
  name: 'SettingsPaymentHistoryView',
  components: {
    ArrowLink,
  },
  mixins: [ ScrollPagerMixin ],
  data: () => ({
    page: 1,
    isLoading: false,
    hasMore: true,
    list: [],
  }),
  created() {
    this.loadMore();
  },
  methods: {
    formatToYMD,
    formatPrice,
    statusVariant(status) {
      if (status === 'success' || status === 'success_wo_payment') {
        return 'success';
      }

      return status === 'failed' ? 'destructive' : 'secondary';
    },
    loadMore() {
      if (this.isLoading || !this.hasMore) {
        return;
      }

      this.isLoading = true;

      PaymentHistoryService.getList({ page: this.page, limit: LIMIT_PER_PAGE })
        .then(resp => {
          this.list.push(...resp);
          this.hasMore = resp.length === LIMIT_PER_PAGE;

          if (this.hasMore) {
            ++this.page;
            this.$nextTick(() => this.initScrollPager('scrollPagerTrigger', 'loadMore'));
          }

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.isLoading = false);
    },
    openDetails(paymentHistory) {
      this.$fullscreenModal.open(
        this,
        PaymentHistoryDetailsModal,
        () => ({
          paymentHistory,
        }),
      );
    },
  },
};
</script>
